import React from 'react';
import { X, HelpCircle } from 'lucide-react';
import { UserProfile } from '../types';

interface HowDoIModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: UserProfile | null;
}

export default function HowDoIModal({ isOpen, onClose, profile }: HowDoIModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[var(--background)]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-[var(--background)] border-2 border-lcars-blue rounded-xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 shadow-2xl shadow-lcars-blue/20">
        
        {/* LCARS Header */}
        <div className="flex h-12">
          <div className="w-16 bg-lcars-blue rounded-br-full shrink-0"></div>
          <div className="flex-1 flex justify-between items-center px-4 bg-lcars-blue/10 border-b border-lcars-blue">
            <h2 className="text-xl font-bold uppercase tracking-widest text-lcars-blue flex items-center gap-2">
              <HelpCircle className="w-5 h-5" /> Navigation Guide
            </h2>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-lcars-red/20 text-lcars-red rounded transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto max-h-[70vh] space-y-6">
          <p className="text-lcars-gold/80 text-sm uppercase tracking-widest leading-relaxed">
            Welcome to the LCARS Command Center interface. Below is a directory of the available systems and what each button allows you to do:
          </p>

          <div className="space-y-4">
            
            <div className="bg-lcars-blue/5 p-4 rounded border-l-4 border-lcars-blue">
              <h3 className="font-bold text-lcars-blue uppercase tracking-widest mb-1">COMMUNICATIONS</h3>
              <p className="text-sm text-gray-300">Read official ship announcements and chat with other members in Ten Forward.</p>
            </div>

            <div className="bg-lcars-blue/5 p-4 rounded border-l-4 border-lcars-blue">
              <h3 className="font-bold text-lcars-blue uppercase tracking-widest mb-1">CALENDAR</h3>
              <p className="text-sm text-gray-300">View the ship's overall event calendar, sign up for events (RSVP), and see when away missions are scheduled.</p>
            </div>

            <div className="bg-lcars-blue/5 p-4 rounded border-l-4 border-lcars-blue">
              <h3 className="font-bold text-lcars-blue uppercase tracking-widest mb-1">CON DUTY ROSTER</h3>
              <p className="text-sm text-gray-300">View upcoming conventions and volunteer to work specific shifts at the ship's booth.</p>
            </div>

            <div className="bg-lcars-blue/5 p-4 rounded border-l-4 border-lcars-blue">
              <h3 className="font-bold text-lcars-blue uppercase tracking-widest mb-1">OFFICIAL DOCUMENTS</h3>
              <p className="text-sm text-gray-300">Access the ship's bylaws, member handbook, and other critical reference files.</p>
            </div>

            <div className="bg-lcars-blue/5 p-4 rounded border-l-4 border-lcars-blue">
              <h3 className="font-bold text-lcars-blue uppercase tracking-widest mb-1">MEMORY ALPHA</h3>
              <p className="text-sm text-gray-300">The central dashboard where you can see your total points, upcoming events you're attending, and quick links.</p>
            </div>

            <div className="bg-lcars-blue/5 p-4 rounded border-l-4 border-lcars-blue">
              <h3 className="font-bold text-lcars-blue uppercase tracking-widest mb-1">YOUR PERSONNEL FILE (Bottom Button)</h3>
              <p className="text-sm text-gray-300">Clicking on your name at the very bottom allows you to view your promotion progress, edit your profile information, and upload your photo.</p>
            </div>

            {(profile?.roles?.includes('admin') || profile?.roles?.includes('dept_lead') || profile?.roles?.includes('inventory')) && (
              <div className="mt-8 pt-4 border-t border-lcars-pink/30">
                <h3 className="text-lcars-pink font-bold uppercase tracking-widest mb-4">Command Staff Functions</h3>
                
                <div className="space-y-4">
                  {profile?.roles?.includes('dept_lead') && (
                    <div className="bg-lcars-pink/5 p-4 rounded border-l-4 border-lcars-pink">
                      <h3 className="font-bold text-lcars-pink uppercase tracking-widest mb-1">MONTHLY REPORT</h3>
                      <p className="text-sm text-gray-300">Department Leads use this to submit their monthly status reports to the command staff.</p>
                    </div>
                  )}
                  {profile?.roles?.includes('admin') && (
                    <>
                      <div className="bg-lcars-pink/5 p-4 rounded border-l-4 border-lcars-pink">
                        <h3 className="font-bold text-lcars-pink uppercase tracking-widest mb-1">MISSION ADMIN</h3>
                        <p className="text-sm text-gray-300">Create, edit, and delete events from the ship's calendar.</p>
                      </div>
                      <div className="bg-lcars-pink/5 p-4 rounded border-l-4 border-lcars-pink">
                        <h3 className="font-bold text-lcars-pink uppercase tracking-widest mb-1">COMMAND DECK</h3>
                        <p className="text-sm text-gray-300">Manage personnel files, review promotions, assign system access, and handle help desk tickets.</p>
                      </div>
                    </>
                  )}
                  {profile?.roles?.includes('inventory') && (
                    <div className="bg-lcars-pink/5 p-4 rounded border-l-4 border-lcars-pink">
                      <h3 className="font-bold text-lcars-pink uppercase tracking-widest mb-1">INVENTORY & SCENES</h3>
                      <p className="text-sm text-gray-300">Quartermasters use this to manage ship props, convention backdrops, and other physical assets.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* LCARS Footer */}
        <div className="flex h-8 mt-2">
          <div className="flex-1 bg-lcars-blue/10 border-t border-lcars-blue flex justify-end px-4 items-center">
            <span className="text-[0.5rem] text-lcars-blue font-mono tracking-widest">END OF DIRECTORY</span>
          </div>
          <div className="w-16 bg-lcars-blue rounded-tl-full shrink-0"></div>
        </div>
      </div>
    </div>
  );
}
